<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpAvatar,
  GpBtn,
  GpMenu,
  GpMenuItemAction,
  GpMenuItemContent,
  GpMenuItemDivider,
  GpMenuItemGroupHeader,
  GpMenuItemStatic,
  GpTooltip,
} from '@gportal/uikit';
import CurrentProjectAvatar from '@panel/components/app/CurrentProjectAvatar.vue';
import ConfirmProjectInviteDeclineModal from '@panel/components/project/invite/ConfirmProjectInviteDeclineModal.vue';
import useProjectInviteAccept from '@panel/components/project/invite/composables/useProjectInviteAccept';
import useProjectInviteDecline from '@panel/components/project/invite/composables/useProjectInviteDecline';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type { BasicProject } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import useRouterGpMenu from 'gpcore-uikit/composables/useRouterGpMenu';
import { computed, ref, useAttrs } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const projectSwitcher = ref<InstanceType<typeof GpMenu>>();

const projectStore = useProjectStore();

const projects = computed(() => projectStore.projects);
const selectableProjects = computed(() =>
  projects.value.filter((p) => p.id !== projectStore.projectId),
);
const currentProject = computed(() => projectStore.project);
const switchProject = (id: string) => {
  projectStore.changeProject(id);
  router.push({ name: 'ProjectMembers', params: { projectId: id } });
  projectSwitcher.value.hideMenuBody();
};

// invites
const invites = computed(() => projectStore.invites);

const { isExecutingInviteAccept, executeProjectInviteAccept } = useProjectInviteAccept();

const {
  inviteDeclineProject,
  inviteDeclineModalOpened,
  isExecutingInviteDecline,
  declineProject,
  executeProjectInviteDecline,
  abortInviteDecline,
} = useProjectInviteDecline();

const isProcessingInvite = computed(
  () => isExecutingInviteAccept.value || isExecutingInviteDecline.value,
);
const processInvite = async (invite: BasicProject, accept: boolean) => {
  if (accept) {
    await executeProjectInviteAccept(invite);

    if (
      projectStore.projects.length === 1 &&
      projectStore.current != null &&
      projectStore.current.length > 0
    ) {
      router.push({
        name: 'ProjectMembers',
        params: { projectId: projectStore.current },
      });
    }
  } else {
    declineProject(invite);
  }
};

const { navigateViaGpMenu } = useRouterGpMenu(projectSwitcher);

const attrs = useAttrs();
</script>

<template>
  <GpMenu :class="attrs.class" body-toggle-width ref="projectSwitcher">
    <template #toggle="{ toggle, isOpened }">
      <GpBtn
        @click="toggle"
        class="tw-flex tw-w-full tw-items-center tw-gap-2 !tw-px-4"
        size="sm"
        v-playwright="'project-switcher-toggle'"
      >
        <CurrentProjectAvatar v-if="currentProject" style="--gp-avatar-size: 20px" />

        <span
          class="tw-flex-1 tw-overflow-hidden tw-overflow-ellipsis tw-text-left"
          v-playwright="'project-switcher-current-project'"
        >
          <template v-if="currentProject">
            {{ currentProject.name }}
          </template>
          <template v-else-if="projects.length > 1">Select a project</template>
          <template v-else-if="projects.length === 0">
            <FontAwesomeIcon :icon="['fas', 'fa-plus']" class="tw-mr-2" />
            Project
          </template>
        </span>

        <GpTooltip v-if="invites.length > 0" content="You have pending project invites.">
          <span
            class="tw-box-border tw-inline-block tw-h-5 tw-min-w-5 tw-rounded-full tw-bg-black tw-text-center tw-text-xs tw-font-bold tw-leading-5 tw-text-gp-cyan"
            >{{ invites.length }}</span
          >
        </GpTooltip>

        <FontAwesomeIcon
          :icon="['fas', 'fa-chevron-up']"
          class="tw-block tw-transition-transform"
          :class="{
            'tw-rotate-180': !isOpened,
            'tw-rotate-0': isOpened,
          }"
        />
      </GpBtn>
    </template>
    <template #default>
      <!-- projects -->
      <GpMenuItemGroupHeader v-if="selectableProjects.length > 0" label="Projects" dense>
        Projects
      </GpMenuItemGroupHeader>
      <GpMenuItemAction
        v-for="project in selectableProjects"
        :key="project.id"
        v-slot="{ contentClass }"
      >
        <GpMenuItemContent
          component="a"
          type="button"
          :class="[contentClass, 'tw-flex']"
          v-playwright="`project-switcher-project-${project.name}`"
          @click="switchProject(project.id)"
        >
          <GpAvatar
            :src="project.avatarUrl"
            :alt="project.name"
            custom-size
            style="--gp-avatar-size: 20px"
          />

          <span class="tw-truncate">
            {{ project.name }}
          </span>
        </GpMenuItemContent>
      </GpMenuItemAction>

      <!-- invites -->
      <GpMenuItemGroupHeader v-if="invites.length > 0" label="Invites" dense />
      <GpMenuItemStatic v-slot="{ contentClass }" v-for="invite in invites" :key="invite.id">
        <div :class="contentClass" class="tw-flex tw-items-center tw-gap-4">
          <div class="tw-flex tw-flex-1 tw-items-center tw-gap-4">
            <GpAvatar
              :src="invite.project.avatarUrl"
              :alt="invite.project.name"
              custom-size
              style="--gp-avatar-size: 20px"
            />
            <span class="tw-text-wrap tw-break-normal">
              {{ invite.project.name }}
            </span>
          </div>

          <div class="tw-flex tw-gap-2">
            <GpTooltip content="Accept invite">
              <GpBtn
                class="!tw-h-6 !tw-w-6 !tw-p-0"
                outline
                size="sm"
                :icon="['fas', 'fa-check']"
                btn-type="success"
                :disabled="isProcessingInvite"
                @click="processInvite(invite.project, true)"
              />
            </GpTooltip>
            <GpTooltip content="Decline invite">
              <GpBtn
                class="!tw-h-6 !tw-w-6 !tw-p-0"
                outline
                size="sm"
                btn-type="danger"
                :icon="['fas', 'fa-times']"
                :disabled="isProcessingInvite"
                @click="declineProject(invite.project)"
              />
            </GpTooltip>
          </div>
        </div>
      </GpMenuItemStatic>

      <GpMenuItemDivider v-if="selectableProjects.length > 0 || invites.length > 0" />

      <GpMenuItemAction v-if="projects.length > 1" v-slot="{ contentClass }">
        <router-link :to="{ name: 'ProjectList' }" v-slot="{ href, navigate }" custom>
          <GpMenuItemContent
            component="a"
            :href="href"
            :class="contentClass"
            label="Projects"
            :icon="['fas', 'fa-briefcase']"
            @click="($event) => navigateViaGpMenu($event, navigate)"
          />
        </router-link>
      </GpMenuItemAction>

      <GpMenuItemAction v-slot="{ contentClass }">
        <router-link :to="{ name: 'ProjectCreate' }" v-slot="{ href, navigate }" custom>
          <GpMenuItemContent
            component="a"
            :href="href"
            :class="contentClass"
            label="Create new project"
            :icon="['fas', 'fa-plus']"
            @click="($event) => navigateViaGpMenu($event, navigate)"
          />
        </router-link>
      </GpMenuItemAction>
    </template>
  </GpMenu>

  <ConfirmProjectInviteDeclineModal
    :is-opened="inviteDeclineModalOpened"
    :project-name="inviteDeclineProject?.name"
    @decline="abortInviteDecline"
    @confirm="executeProjectInviteDecline"
  />
</template>
